import { FC, Suspense, useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { CurrentAssessment } from './IndividualReportAssessments';
import { reviewAssessmentRegions } from '../../learnosity-regions/ReviewAssessmentRegions';
import { useIndividualReportInitialization } from '../../custom-hooks/useIndividualReportInitialization';
import { useLearnosityItemsInitialization } from '../../custom-hooks/useLearnosityItemsInitialization';
import { ErrorBoundaryUI } from '../ErrorBoundary/ErrorBoundaryUI';
import CustomErrorBoundary from '../LayoutWrapper/CustomErrorBoundary';
import { LearnosityReportAccessDeniedError } from '../../utils/learnosityErrors';
import { IndividualReportAccessDenied } from './IndividualReportAccessDenied';

interface Props {
  currentAssessmentInfo: CurrentAssessment;
}

const IndividualReportAssessmentContent: FC<Props & { fetchKey: string }> = ({ currentAssessmentInfo, fetchKey }) => {
  const [failure, setFailure] = useState<unknown>();
  const variables = useMemo(
    () => ({
      skip: currentAssessmentInfo === null,
      initModel: {
        sessionId: currentAssessmentInfo?.sessionId || '',
        activityId: currentAssessmentInfo?.activityId || '',
        regions: JSON.stringify(reviewAssessmentRegions),
        autoSaveAnswers: false,
        showCorrectAnswers: true,
        showDistractorRationale: false,
        responsiveRegions: false,
        state: 'review',
        renderingType: 'assess',
        userId: currentAssessmentInfo?.userId || '',
        title: currentAssessmentInfo.name,
        scrollToTop: false,
        scrollToTest: false,
        showIntro: false,
        skipSubmitConfirmation: false,
        warningOnChange: false,
      },
    }),
    [
      currentAssessmentInfo.sessionId,
      currentAssessmentInfo.activityId,
      currentAssessmentInfo.userId,
      currentAssessmentInfo.name,
    ],
  );
  const { initialization, refetchInitialization, initializationError } = useIndividualReportInitialization(
    variables,
    fetchKey,
  );
  const { status } = useLearnosityItemsInitialization({
    initObj: initialization,
    assessmentType: 'individual_report',
    reloadOn10001AfterReady: true,
    refetchInitialization,
    getContainer: () => document.getElementById('learnosity_assess'),
    onReady: () => {},
    onFailure: error => setFailure(error),
  });
  const failed = status === 'failed' || !!initializationError;
  const accessDenied =
    initializationError instanceof LearnosityReportAccessDeniedError ||
    failure instanceof LearnosityReportAccessDeniedError;
  return (
    <div id="singleSkillAssessment">
      {failed && (accessDenied ? <IndividualReportAccessDenied /> : <ErrorBoundaryUI embedded />)}
      <div hidden={failed}>
        <div id="learnosity_assess"></div>
      </div>
    </div>
  );
};

const IndividualReportAssessmentOpening: FC<Props> = ({ currentAssessmentInfo }) => {
  // Commit the opening key even while its query suspends; rerenders keep it stable.
  const [fetchKey] = useState(() => uuid());
  return (
    <Suspense>
      <IndividualReportAssessmentContent currentAssessmentInfo={currentAssessmentInfo} fetchKey={fetchKey} />
    </Suspense>
  );
};

export const IndividualReportAssessmentPlayer: FC<Props> = ({ currentAssessmentInfo }) => (
  <CustomErrorBoundary
    key={JSON.stringify([
      currentAssessmentInfo.userId,
      currentAssessmentInfo.activityId,
      currentAssessmentInfo.sessionId,
    ])}
    fallback={<ErrorBoundaryUI embedded />}
  >
    <IndividualReportAssessmentOpening currentAssessmentInfo={currentAssessmentInfo} />
  </CustomErrorBoundary>
);
